import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories as allCategories } from '@/mocks/articles';
import { generateBreadcrumbSchema } from '@/mocks/schema';

const BASE_URL = 'https://yuriclub.cn';
const ARTICLES_PER_PAGE = 9;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const category = useMemo(() => allCategories.find((c) => c.slug === slug), [slug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [slug]);

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug]
  );

  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到 | 百合会"
          description="抱歉，您访问的分类页面不存在。"
          keywords="百合会"
          canonicalUrl={`${BASE_URL}/category/${slug}`}
        />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，你访问的分类页面不存在</p>
            <Link to="/" className="px-6 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 百合会 | 百合文化爱好者社区`;
  const pageDesc = `${category.description}。百合会精选${category.name}相关内容，为百合文化爱好者提供优质阅读体验。`;
  const pageKeywords = `百合会,${category.name},百合漫画推荐,百合动漫作品,百合小说,百合文化`;
  const canonicalUrl = `${BASE_URL}/category/${slug}`;

  const jsonLd = [
    generateBreadcrumbSchema(
      [
        { label: '首页', href: '/' },
        { label: category.name },
      ],
      BASE_URL
    ),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={canonicalUrl}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-14 md:pb-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto mt-6">
          <div className="mb-8 md:mb-10">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {category.description}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {allCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分类下暂无文章</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {paginatedArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden cursor-pointer"
                  >
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime} 分钟
                        </span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页导航">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-lg flex items-center justify-center border border-background-200/70 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-foreground-700"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                        page === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-200/70 text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-lg flex items-center justify-center border border-background-200/70 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-foreground-700"></i>
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}